// Compare tab of the BOM editor: two variants of this family, or one variant in two revisions, part by
// part (phantoms exploded, stocked sub-assemblies as one part): what B adds, removes or changes in
// quantity, supply or cost against A. compareResolved() in shared/boms.ts does the work.
import { useMemo, useState } from "react";
import { type BomIndex, type BomRow, type CompareChange, compareResolved, familyKey, resolve, revisionState, SUPPLY_LABELS } from "../../../shared/boms.ts";
import type { Item } from "../../../shared/db.gen.ts";
import { cost2 } from "../lib/boms.ts";
import { money, qty } from "../lib/format.ts";
import { Badge, cx, EmptyState, Select } from "../ui/controls.tsx";
import { type Column, DataTable } from "../ui/DataTable.tsx";

const CHANGE: Record<CompareChange, { label: string; tone: "ok" | "danger" | "accent" | "warn" | "neutral" }> = {
  added: { label: "added", tone: "ok" },
  removed: { label: "removed", tone: "danger" },
  qty: { label: "quantity", tone: "accent" },
  supply: { label: "supply", tone: "warn" },
  cost: { label: "cost", tone: "neutral" },
  same: { label: "same", tone: "neutral" },
};

const signed = (n: number | null) => (n == null ? "" : Math.abs(n) < 0.005 ? cost2(0) : `${n > 0 ? "+" : "−"}${cost2(Math.abs(n))}`);

export function CompareTab({ ix, bom, variants }: { ix: BomIndex; bom: BomRow; variants: Item[] }) {
  const family = ix.families.get(familyKey(bom)) ?? [];
  const product = !!bom.shopify_product_id;
  const [mode, setMode] = useState<"variants" | "revisions">(variants.length > 1 ? "variants" : "revisions");
  const [va, setVa] = useState(variants[0]?.id ?? "");
  const [vb, setVb] = useState(variants[1]?.id ?? variants[0]?.id ?? "");
  const older = family.find((r) => r.revision < bom.revision) ?? family.find((r) => r.id !== bom.id);
  const [ra, setRa] = useState(older?.id ?? bom.id);
  const [rb, setRb] = useState(bom.id);
  const [variant, setVariant] = useState(variants[0]?.id ?? "");
  const [showSame, setShowSame] = useState(false);

  const result = useMemo(() => {
    const item = (id: string) => ix.itemById.get(id);
    if (mode === "variants") return { comparison: compareResolved(resolve(ix, bom, item(va)), resolve(ix, bom, item(vb))), differing: null };
    const [a, b] = [ix.bomById.get(ra), ix.bomById.get(rb)];
    if (!a || !b) return null;
    // How many variants the two revisions differ in, and the chosen one's parts.
    const differing = variants.filter((v) => {
      const c = compareResolved(resolve(ix, a, item(v.id)), resolve(ix, b, item(v.id)));
      return Object.values(c.counts).some(Boolean);
    }).length;
    return { comparison: compareResolved(resolve(ix, a, item(variant)), resolve(ix, b, item(variant))), differing };
  }, [ix, bom, mode, va, vb, ra, rb, variant, variants]);

  const rows = useMemo(
    () => (result?.comparison.rows ?? []).filter((r) => showSame || r.change !== "same").map((r, i) => ({ ...r, id: r.item.id, order: i })),
    [result, showSame],
  );
  type Row = (typeof rows)[number];
  const columns = useMemo<Column<Row>[]>(
    () => [
      {
        key: "order",
        header: "Change",
        width: "100px",
        value: (r) => r.order,
        cell: (r) => <Badge tone={CHANGE[r.change].tone}>{CHANGE[r.change].label}</Badge>,
      },
      { key: "sku", header: "SKU", width: "170px", value: (r) => r.item.sku, cell: (r) => <span className="font-mono text-[12px]">{r.item.sku}</span> },
      { key: "name", header: "Name", width: "minmax(240px,2fr)", value: (r) => r.item.name },
      { key: "qa", header: "Qty A", width: "80px", align: "right", value: (r) => r.a?.qty ?? 0, cell: (r) => (r.a ? qty(r.a.qty) : "—") },
      {
        key: "qb",
        header: "Qty B",
        width: "80px",
        align: "right",
        value: (r) => r.b?.qty ?? 0,
        cell: (r) => <span className={cx(r.change === "qty" && "font-medium text-accent-text")}>{r.b ? qty(r.b.qty) : "—"}</span>,
      },
      {
        key: "supply",
        header: "Supply",
        width: "190px",
        value: (r) => (r.b ?? r.a)?.supply ?? "",
        cell: (r) =>
          r.a && r.b && r.a.supply !== r.b.supply
            ? `${SUPPLY_LABELS[r.a.supply]} → ${SUPPLY_LABELS[r.b.supply]}`
            : SUPPLY_LABELS[(r.b ?? r.a)?.supply ?? "hhl_consigns"],
      },
      { key: "ua", header: "Unit A", width: "90px", align: "right", value: (r) => r.a?.unitCost ?? null, cell: (r) => money(r.a?.unitCost) },
      { key: "ub", header: "Unit B", width: "90px", align: "right", value: (r) => r.b?.unitCost ?? null, cell: (r) => money(r.b?.unitCost) },
      {
        key: "delta",
        header: "Cost change",
        width: "110px",
        align: "right",
        value: (r) => r.delta,
        cell: (r) => (
          <span className={cx("font-mono", (r.delta ?? 0) > 0.005 ? "text-danger" : (r.delta ?? 0) < -0.005 ? "text-ok" : "text-faint")}>
            {signed(r.delta)}
          </span>
        ),
      },
    ],
    [],
  );

  if (variants.length < 2 && family.length < 2)
    return (
      <EmptyState title="Nothing to compare yet">
        This BOM has one revision{product ? " and one variant" : ""}. Start a new revision to compare against it.
      </EmptyState>
    );

  const variantOptions = variants.map((v) => ({ value: v.id, label: v.shopify_variant_title || v.sku || v.name }));
  const revisionOptions = family.map((r) => ({ value: r.id, label: `Rev ${r.revision} · ${revisionState(ix, r)}` }));
  const c = result?.comparison;
  const changed = c ? Object.values(c.counts).reduce((n, x) => n + x, 0) : 0;
  const pick = (label: string, value: string, set: (v: string) => void, options: { value: string; label: string }[]) => (
    <label className="flex items-center gap-1.5 whitespace-nowrap">
      <span className="text-muted">{label}</span>
      <Select value={value} onChange={(e) => set(e.target.value)} options={options} className="h-7 w-56 text-[12px]" />
    </label>
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1.5 border-b border-line px-4 py-2 text-[12px]">
        <div className="flex rounded-md border border-line p-0.5">
          {(["variants", "revisions"] as const).map((m) => (
            <button
              key={m}
              type="button"
              disabled={m === "variants" ? variants.length < 2 : family.length < 2}
              onClick={() => setMode(m)}
              className={cx("rounded px-2 py-0.5 disabled:opacity-40", mode === m ? "bg-accent-soft text-accent-text" : "text-muted hover:text-text")}
            >
              {m === "variants" ? "Two variants" : "Two revisions"}
            </button>
          ))}
        </div>
        {mode === "variants" ? (
          <>
            {pick("A", va, setVa, variantOptions)}
            {pick("B", vb, setVb, variantOptions)}
          </>
        ) : (
          <>
            {pick("A", ra, setRa, revisionOptions)}
            {pick("B", rb, setRb, revisionOptions)}
            {product && pick("Variant", variant, setVariant, variantOptions)}
          </>
        )}
        <label className="ml-auto flex cursor-pointer items-center gap-1.5">
          <input type="checkbox" checked={showSame} onChange={(e) => setShowSame(e.target.checked)} className="accent-[var(--accent)]" />
          <span className="text-muted">Show unchanged</span>
        </label>
      </div>
      {c && (
        <div className="flex shrink-0 flex-wrap items-center gap-x-5 gap-y-1 border-b border-line px-4 py-2 text-[12px]">
          <span className="text-muted">
            Cost A <span className="font-mono text-text">{cost2(c.a.total)}</span>
            {c.a.complete ? "" : "+"}
          </span>
          <span className="text-muted">
            Cost B <span className="font-mono text-text">{cost2(c.b.total)}</span>
            {c.b.complete ? "" : "+"}
          </span>
          <span className="text-muted">
            Change{" "}
            <span className={cx("font-mono", c.b.total - c.a.total > 0.005 ? "text-danger" : c.b.total - c.a.total < -0.005 ? "text-ok" : "text-text")}>
              {signed(c.b.total - c.a.total)}
            </span>
          </span>
          {!changed && <Badge tone="ok">Same parts</Badge>}
          {c.counts.added > 0 && <Badge tone="ok">{c.counts.added} added</Badge>}
          {c.counts.removed > 0 && <Badge tone="danger">{c.counts.removed} removed</Badge>}
          {c.counts.qty > 0 && <Badge tone="accent">{c.counts.qty} quantity</Badge>}
          {c.counts.supply > 0 && <Badge tone="warn">{c.counts.supply} supply</Badge>}
          {c.counts.cost > 0 && <Badge>{c.counts.cost} cost</Badge>}
          {result?.differing != null && product && (
            <span className="text-faint">
              The revisions differ in {result.differing} of {variants.length} variants
            </span>
          )}
          {(!c.a.complete || !c.b.complete) && <span className="text-faint">+ = some parts have no cost</span>}
        </div>
      )}
      <div className="min-h-0 flex-1">
        <DataTable rows={rows} columns={columns} defaultSort={{ key: "order" }} rowNoun={["part", "parts"]} exportName="BOM compare" pageFilters={false} />
      </div>
    </div>
  );
}
